import { useEffect, useMemo, useState } from 'react';
import captureVideoFrames from '../../../utils/captureVideoFrames';
import {
  getKlydoStillFrameUrl,
  THUMBNAIL_FILMSTRIP_WIDTH,
} from '../../../utils/klydoThumbnail';
import { FilmstripFrame } from '../components/KlydoThumbnailFilmstrip';
import { ScrubVideo } from './useScrubVideo';

type UseFilmstripFramesProps = {
  loopUrl: string;
  scrubVideo?: ScrubVideo;
  offsetsSeconds: number[];
};

// Frames are cut in the browser from the downloaded scrub copy. Asking
// Cloudinary for them costs one cold derivation of the full original per frame.
const useFilmstripFrames = ({
  loopUrl,
  scrubVideo,
  offsetsSeconds,
}: UseFilmstripFramesProps): FilmstripFrame[] => {
  const [capturedUrls, setCapturedUrls] = useState<string[]>();
  const [captureFailed, setCaptureFailed] = useState(false);
  const localVideoUrl = scrubVideo?.isLocalCopy ? scrubVideo.url : undefined;
  const useCloudinaryStills =
    captureFailed || scrubVideo?.isLocalCopy === false;

  useEffect(() => {
    setCapturedUrls(undefined);
    setCaptureFailed(false);
    if (!localVideoUrl || offsetsSeconds.length === 0) return;

    let cancelled = false;
    captureVideoFrames({
      videoUrl: localVideoUrl,
      offsetsSeconds,
      width: THUMBNAIL_FILMSTRIP_WIDTH,
    }).then(
      (urls) => {
        if (!cancelled) setCapturedUrls(urls);
      },
      () => {
        if (!cancelled) setCaptureFailed(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [localVideoUrl, offsetsSeconds]);

  return useMemo(
    () =>
      offsetsSeconds.map((offsetSeconds, index) => ({
        offsetSeconds,
        url: useCloudinaryStills
          ? getKlydoStillFrameUrl({
              loopUrl,
              offsetSeconds,
              width: THUMBNAIL_FILMSTRIP_WIDTH,
            })
          : capturedUrls?.[index],
      })),
    [offsetsSeconds, capturedUrls, useCloudinaryStills, loopUrl],
  );
};

export default useFilmstripFrames;
